import { useMemo } from 'react';
import { RaporTablosu } from '../rapor-ekrani/RaporTablosu';
import { paraMetni, sayiMetni } from '../rapor-ekrani/bicim';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import type { RaporKolonu } from '../rapor-ekrani/kolon';
import { Kart, OdemeSeridi, Rozet, sureHucresi } from './gorsel';
import { CINS_ADI, type BolumSatiri, type TarafOzeti } from './tipler';

interface Props {
  ozet: TarafOzeti;
  /** Bölüm satırına basınca: o tarafın o bölümünün adisyonları çekmecede. */
  onBolumAc: (kimlik: string) => void;
  seciliKimlik: string | null;
  yukleniyor: boolean;
  yenileniyor: boolean;
}

/**
 * S340: bir tarafın (Bayan / Erkek) kartı. Üstte rozet ve sayılar, ortada ciro ile nakit / kart şeridi,
 * altta Giriş kasası / Salon / VİP satırları. Adisyonu olmayan bölüm satır olmaz.
 */
export function TarafKarti({ ozet, onBolumAc, seciliKimlik, yukleniyor, yenileniyor }: Props) {
  const { cins, toplam, bolumler } = ozet;
  const ad = CINS_ADI[cins];
  const telefon = useDarEkran();

  // `kart`: telefonda bölüm kartı. Üstte bölüm, sağında ciro; altında adisyon ve kalış, sonra nakit / kart.
  const kolonlar = useMemo<RaporKolonu<BolumSatiri>[]>(() => [
    { id: 'bolum', baslik: 'Bölüm', tur: 'metin', deger: (b) => b.ad, esnek: true, kart: 'baslik' },
    // Dar genişlikler ÖLÇÜLDÜ: 1440'ta yan yana iki kartın içi 561 px; altı kolon veriden ölçülünce 578 px tutuyordu.
    { id: 'adisyon', baslik: 'Adisyon', tur: 'sayi', genislik: 68, deger: (b) => b.adisyon, kart: 'alt', kartEtiketi: 'Adisyon' },
    { id: 'tutar', baslik: 'Ciro', tur: 'para', deger: (b) => b.tutar, kart: 'olcu' },
    { id: 'nakit', baslik: 'Nakit', tur: 'para', deger: (b) => b.nakit, kart: 'ikincil' },
    { id: 'kart', baslik: 'Kart', tur: 'para', deger: (b) => b.kart, kart: 'ikincil' },
    {
      id: 'kalis', baslik: 'Ort. kalış', tur: 'sayi', genislik: 84, kart: 'olcuAlt', kartEtiketi: 'Ort. kalış',
      deger: (b) => b.kalis,
      hucre: sureHucresi,
    },
  ], []);

  return (
    <Kart
      testid={`taraf-${cins}`}
      baslik={<Rozet cins={cins} />}
      araclar={
        <span className="text-[13px] text-ink-500" data-testid={`taraf-${cins}-sayilar`}>
          {yukleniyor ? '…' : `${sayiMetni(toplam.adisyon)} adisyon · ${sayiMetni(toplam.yikama)} yıkama · ${sayiMetni(toplam.tellak)} tellak hizmeti`}
        </span>
      }
    >
      <div className="mono text-[24px] font-bold leading-tight text-ink-900" data-testid={`taraf-${cins}-ciro`}>
        {yukleniyor ? '…' : paraMetni(toplam.tutar)}
      </div>
      <OdemeSeridi nakit={toplam.nakit} kart={toplam.kart} />
      {/* Masaüstünde tablo kartın kenarından kenarına: yan dolgu kolonlara kalır (yatay kaydırma çıkmasın).
          Telefonda kart listesi kartın dolgusunun içinde kalır. */}
      <div className={telefon ? 'mt-3' : 'mt-3 -mx-3.5'}>
        <RaporTablosu
          testid={`taraf-${cins}-bolumler`}
          className={telefon ? '' : '!border-x-0 !rounded-none'}
          etiket={`${ad} bölümleri`}
          kartStili="cizgi"
          kolonlar={kolonlar}
          satirlar={bolumler}
          satirKimligi={(b) => b.kimlik}
          onSatirTikla={(b) => onBolumAc(b.kimlik)}
          seciliKimlik={seciliKimlik}
          bosMesaj="Bu aralıkta adisyon yok"
          yukleniyor={yukleniyor}
          yenileniyor={yenileniyor}
        />
      </div>
    </Kart>
  );
}
